import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import userServices from "../../api/services/userService";
import loginSessionsService from "../../api/services/loginSessionsService";
import { confirmDanger, confirmDialog } from "../../utils/confirmDialog";
import {
  FilterSection,
  ActiveFilterPills,
  UserProfilesTable,
  UserProfilesPagination,
  EditUserProfileModal,
  CreateAppUserModal,
  useUserProfiles,
} from "../../components/UserProfiles";
import { getAdminPanelRole, canEditAppUserProfile } from "../../utils/adminPanelRole";
import UserLoginDevicesModal from "../LoginSessions/UserLoginDevicesModal";

export default function UserProfiles() {
  const navigate = useNavigate();
  const {
    filters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    secondaryFields,
    users,
    total,
    isLoading,
    backendPage,
    setBackendPage,
    backendLimit,
    fetchFilteredUsers,
  } = useUserProfiles();

  const [editUserId, setEditUserId] = useState(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [devicesUser, setDevicesUser] = useState(null);
  const [logoutBusyUserId, setLogoutBusyUserId] = useState(null);
  const [bulkLogoutBusy, setBulkLogoutBusy] = useState(false);
  const [globalLogoutBusy, setGlobalLogoutBusy] = useState(false);

  const adminRoleKey = getAdminPanelRole();

  /** Create app user: Admin + Developer only (CSR uses edit only). */
  const canCreateAppUser = adminRoleKey === "admin" || adminRoleKey === "developer";

  /** Edit app user profile: Admin, Developer, CSR, CSR Team Lead. */
  const canEditUserProfile = canEditAppUserProfile(adminRoleKey);

  const canDeleteAppUser = adminRoleKey === "developer" || adminRoleKey === "admin";
  const canManageLoginDevices =
    adminRoleKey === "admin" || adminRoleKey === "developer";

  useEffect(() => {
    setSelectedIds([]);
  }, [backendPage]);

  const toggleSelectId = useCallback((id) => {
    if (id == null) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    const pageIds = (users || []).map((u) => u.id).filter(Boolean);
    if (!pageIds.length) return;
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  }, [users, selectedIds]);

  const handleApply = () => {
    setBackendPage(1);
  };

  const handleViewProfile = (id) => {
    if (!id) return;
    navigate(`/userdetails/${id}`);
  };

  const handleEditUser = useCallback((id) => {
    setEditUserId(id);
  }, []);

  const handleEditClose = useCallback(() => setEditUserId(null), []);

  const handleUserSaved = useCallback(() => {
    return fetchFilteredUsers(filters, backendPage, { refresh: true });
  }, [fetchFilteredUsers, filters, backendPage]);

  const handleDeleteUser = useCallback(
    async (userRow) => {
      const id = userRow?.id;
      if (!id) return;
      const name = `${userRow.firstName || ""} ${userRow.lastName || ""}`.trim() || `User #${id}`;
      const ok = await confirmDanger(
        `Permanently delete ${name} (ID ${id})? This removes the account and related data and cannot be undone.`,
        "Delete app user?"
      );
      if (!ok) return;
      setDeletingId(id);
      try {
        const res = await userServices.deleteAppUserByDeveloper(id);
        if (res?.success) {
          toast.success(res?.message || "User deleted");
          setSelectedIds((prev) => prev.filter((x) => x !== id));
          fetchFilteredUsers(filters, backendPage, { refresh: true });
        } else {
          toast.error(res?.message || "Delete failed");
        }
      } catch (err) {
        const msg =
          (err && typeof err === "object" && err.message) ||
          (typeof err === "string" ? err : null) ||
          "Failed to delete user";
        toast.error(msg);
      } finally {
        setDeletingId(null);
      }
    },
    [fetchFilteredUsers, filters, backendPage]
  );

  const handleBulkDeleteUsers = useCallback(async () => {
    if (!selectedIds.length) return;
    const ok = await confirmDanger(
      `Permanently delete ${selectedIds.length} selected user(s)? This cannot be undone.`,
      "Delete selected users?"
    );
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const res = await userServices.bulkDeleteAppUsers(selectedIds);
      if (res?.success) {
        toast.success(res?.message || "Users deleted");
        setSelectedIds([]);
        fetchFilteredUsers(filters, backendPage, { refresh: true });
      } else {
        toast.error(res?.message || "Bulk delete failed");
      }
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err && typeof err === "object"
            ? err.message
            : null;
      toast.error(msg || "Failed to delete users");
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedIds, fetchFilteredUsers, filters, backendPage]);

  const handleOpenLoginDevices = useCallback((userRow) => {
    if (!userRow?.id) return;
    setDevicesUser({
      userId: userRow.id,
      id: userRow.id,
      name:
        `${userRow.firstName || ""} ${userRow.lastName || ""}`.trim() || null,
      firstName: userRow.firstName,
      lastName: userRow.lastName,
      email: userRow.email,
    });
  }, []);

  const handleLogoutAllDevices = useCallback(async (userRow) => {
    const id = userRow?.id;
    if (!id) return;
    const name =
      `${userRow.firstName || ""} ${userRow.lastName || ""}`.trim() ||
      userRow.email ||
      `User #${id}`;
    const ok = await confirmDanger(
      `Log out ${name} from every device? They will need to sign in again.`,
      "Logout all devices?"
    );
    if (!ok) return;
    setLogoutBusyUserId(id);
    try {
      await loginSessionsService.forceLogoutAllForUser(id);
      toast.success("All devices logged out");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Logout all failed"
      );
    } finally {
      setLogoutBusyUserId(null);
    }
  }, []);

  const handleBulkLogoutAllDevices = useCallback(async () => {
    if (!selectedIds.length) return;
    const ok = await confirmDanger(
      `Log out all devices for ${selectedIds.length} selected user(s)?`,
      "Logout all selected users?"
    );
    if (!ok) return;
    setBulkLogoutBusy(true);
    let okCount = 0;
    let failCount = 0;
    try {
      for (const id of selectedIds) {
        try {
          await loginSessionsService.forceLogoutAllForUser(id);
          okCount += 1;
        } catch {
          failCount += 1;
        }
      }
      if (okCount) toast.success(`Logged out all devices for ${okCount} user(s)`);
      if (failCount) toast.error(`Failed for ${failCount} user(s)`);
    } finally {
      setBulkLogoutBusy(false);
    }
  }, [selectedIds]);

  const handleForceLogoutAllUsers = useCallback(async () => {
    const ok = await confirmDialog({
      title: "Force logout ALL users?",
      html:
        "<p>This logs out <strong>every user on every device</strong> (including ~60k accounts if that many are logged in).</p>" +
        "<p class='mt-2'>They must sign in again. Type <strong>LOGOUT ALL</strong> to confirm.</p>",
      icon: "warning",
      confirmButtonText: "Force logout everyone",
      confirmButtonColor: "#b91c1c",
      input: "text",
      inputPlaceholder: "LOGOUT ALL",
      inputValidator: (value) => {
        if (String(value || "").trim().toUpperCase() !== "LOGOUT ALL") {
          return "Type LOGOUT ALL to continue";
        }
        return null;
      },
    });
    if (!ok) return;
    setGlobalLogoutBusy(true);
    try {
      const res = await loginSessionsService.forceLogoutAllUsers();
      toast.success(
        res?.message || `Force logged out ${res?.usersAffected ?? 0} user(s)`
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Force logout all users failed"
      );
    } finally {
      setGlobalLogoutBusy(false);
    }
  }, []);

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-6">
        <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
          <h3 className="text-md md:text-xl font-bold text-gray-800">
            User Profiles
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {canManageLoginDevices && (
              <button
                type="button"
                disabled={globalLogoutBusy || bulkLogoutBusy || bulkDeleting}
                onClick={handleForceLogoutAllUsers}
                className="text-xs md:text-sm font-semibold px-3 py-1.5 rounded-lg border border-rose-600 bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Revoke every active login session for all users in one action">
                {globalLogoutBusy ? "Logging out everyone…" : "Force logout all users"}
              </button>
            )}
            {canCreateAppUser && (
              <button
                type="button"
                onClick={() => setCreateUserOpen(true)}
                className="text-xs md:text-sm font-semibold px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600">
                Create user
              </button>
            )}
            {getActiveFiltersCount() > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs md:text-sm text-red-600 hover:text-red-700 font-medium">
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        <ActiveFilterPills
          activeKeys={activeFilterEntries}
          filters={filters}
          getLabel={getFilterLabel}
          onRemove={removeFilter}
        />

        <FilterSection
          primaryFields={primaryFields}
          secondaryFields={secondaryFields}
          values={filters}
          onChange={handleFilterChange}
          onApply={() => {
            setSelectedIds([]);
            handleApply();
          }}
          onClear={clearFilters}
          applyLabel="Apply"
          clearLabel="Clear All"
        />

        {(canDeleteAppUser || canManageLoginDevices) &&
          !isLoading &&
          users.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {canDeleteAppUser && (
              <button
                type="button"
                disabled={!selectedIds.length || bulkDeleting || bulkLogoutBusy}
                onClick={handleBulkDeleteUsers}
                className="text-xs md:text-sm font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {bulkDeleting ? "Deleting…" : `Delete selected (${selectedIds.length})`}
              </button>
            )}
            {canManageLoginDevices && (
              <button
                type="button"
                disabled={!selectedIds.length || bulkDeleting || bulkLogoutBusy}
                onClick={handleBulkLogoutAllDevices}
                className="text-xs md:text-sm font-semibold px-3 py-1.5 rounded-lg border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50 disabled:cursor-not-allowed">
                {bulkLogoutBusy
                  ? "Logging out…"
                  : `Logout all selected (${selectedIds.length})`}
              </button>
            )}
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="text-xs md:text-sm text-gray-600 hover:text-gray-900 font-medium">
                Clear selection
              </button>
            )}
          </div>
        )}

        <UserProfilesTable
          users={users}
          page={backendPage}
          limit={backendLimit}
          isLoading={isLoading}
          activeFiltersCount={getActiveFiltersCount()}
          onViewProfile={handleViewProfile}
          onEditUser={canEditUserProfile ? handleEditUser : undefined}
          onDeleteUser={canDeleteAppUser ? handleDeleteUser : undefined}
          onOpenLoginDevices={
            canManageLoginDevices ? handleOpenLoginDevices : undefined
          }
          onLogoutAllDevices={
            canManageLoginDevices ? handleLogoutAllDevices : undefined
          }
          logoutBusyUserId={logoutBusyUserId}
          deletingUserId={deletingId}
          showRowSelection={canDeleteAppUser || canManageLoginDevices}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelectId}
          onToggleSelectAll={toggleSelectAllOnPage}
          bulkDeleting={bulkDeleting || bulkLogoutBusy}
        />

        <EditUserProfileModal
          userId={editUserId}
          isOpen={editUserId != null}
          onClose={handleEditClose}
          onSaved={handleUserSaved}
        />

        <UserLoginDevicesModal
          open={Boolean(devicesUser)}
          user={devicesUser}
          onClose={() => setDevicesUser(null)}
        />

        <CreateAppUserModal
          isOpen={createUserOpen}
          onClose={() => setCreateUserOpen(false)}
          onCreated={() => {
            setBackendPage(1);
            return fetchFilteredUsers(filters, 1, { refresh: true });
          }}
        />

        {total > 0 && (
          <UserProfilesPagination
            page={backendPage}
            total={total}
            limit={backendLimit}
            onPageChange={setBackendPage}
          />
        )}
      </div>
    </div>
  );
}
